import { useSyncExternalStore } from 'react';
import en from './locales/en.json';
import hy from './locales/hy.json';
import es from './locales/es.json';
import de from './locales/de.json';
import fr from './locales/fr.json';
import pt from './locales/pt.json';
import tr from './locales/tr.json';
import kk from './locales/kk.json';
import uk from './locales/uk.json';
import zh from './locales/zh.json';
import { SCREEN_FRAGMENTS, type ScreenFragment } from './screens';

/** Основные словари и фрагменты экранов (i18n/screens, план вехи 3 S7) одним словарём на язык: фрагмент дописывает свои ключи,
    совпадающий ключ обязан иметь тот же перевод (i18n/index.test.ts), поэтому порядок слияния не меняет итог */
export function mergeDictionaries(
  base: Readonly<Record<string, Readonly<Record<string, string>>>>,
  fragments: Readonly<Record<string, ScreenFragment>>,
): Record<string, Record<string, string>> {
  const out: Record<string, Record<string, string>> = {};
  for (const [lang, dict] of Object.entries(base)) {
    out[lang] = { ...dict };
    for (const fragment of Object.values(fragments)) Object.assign(out[lang], (fragment as Record<string, Record<string, string> | undefined>)[lang]);
  }
  return out;
}

const dictionaries: Record<string, Record<string, string>> = mergeDictionaries({ en, hy, es, de, fr, pt, tr, kk, uk, zh }, SCREEN_FRAGMENTS);
let locale = 'ru';
try {
  const saved = localStorage.getItem('steos:locale');
  if (saved === 'ru' || (saved && dictionaries[saved])) locale = saved;
} catch {
  /* Хранилище необязательно для работы. */
}
const listeners = new Set<() => void>();
export function accountLocale() {
  return locale;
}
export function setAccountLocale(value: string) {
  const next = value === 'ru' || dictionaries[value] ? value : 'ru';
  document.documentElement.lang = next;
  try {
    localStorage.setItem('steos:locale', next);
  } catch {
    /* Сервер хранит подтверждённый язык. */
  }
  if (next === locale) return;
  locale = next;
  listeners.forEach(listener => listener());
  document.dispatchEvent(new Event('steos:locale'));
}
function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}
export function useAccountLocale() {
  return useSyncExternalStore(subscribe, accountLocale, () => 'ru');
}

/** Переводятся только интерфейсные строки. Числа, JSX и данные форм возвращаются без преобразования. */
export function uiText<T>(value: T): T {
  if (typeof value !== 'string' || locale === 'ru') return value;
  const key = value.replace(/\s+/g, ' ').trim();
  const translated = dictionaries[locale]?.[key];
  if (!translated) return value;
  return ((value.match(/^\s*/)?.[0] ?? '') + translated + (value.match(/\s*$/)?.[0] ?? '')) as T;
}
export function uiFormat(template: string, ...values: unknown[]): string {
  return uiText(template).replace(/\{(\d+)\}/g, (_, index: string) => String(values[Number(index)] ?? ''));
}
/** Подпись в тесном месте: у строки свой перевод для этого места (ключ словаря «<место>: <подпись>»), без него — обычный перевод.
    Пример: «Хит» в плашках — Popular, в косой ленте карточки срока — Top (лента рассчитана на три буквы) */
export function uiIn(place: string, value: string): string {
  if (locale === 'ru') return value;
  return dictionaries[locale]?.[place + ': ' + value.replace(/\s+/g, ' ').trim()] ?? uiText(value);
}
/** Подпись пункта меню: у раздела своё короткое имя («Озвучка» в меню — Voices, как раздел голосов; в «Истории» работа — Voiceover) */
export function uiNav(value: string): string {
  return uiIn('меню', value);
}
/** Форма слова при числе n: ru — по правилам русского (one, few, many), другие языки — ключ словаря «one|few|many» (русские формы),
    значение — формы языка через «|» по его правилам (Intl.PluralRules): одна форма (tr, kk, hy после числа), две (one|other), три (uk: one|few|many).
    Перевода нет — русская форма */
const RU_PLURAL = new Intl.PluralRules('ru');
export function uiPlural(n: number, one: string, few: string, many: string): string {
  const ru = () => { const c = RU_PLURAL.select(n); return c === 'one' ? one : c === 'few' ? few : c === 'many' ? many : few; };
  if (locale === 'ru') return ru();
  const forms = dictionaries[locale]?.[`${one}|${few}|${many}`]?.split('|');
  if (!forms?.length) return ru();
  if (forms.length === 1) return forms[0];
  let c: string = 'other';
  try { c = new Intl.PluralRules(locale).select(n); } catch { /* язык без правил — «other» */ }
  if (forms.length === 2) return c === 'one' ? forms[0] : forms[1];
  return c === 'one' ? forms[0] : c === 'few' ? forms[1] : forms[2];
}
/** Дата языка профиля: у русского — как раньше (вызывающий собирает сам), у остальных — Intl.DateTimeFormat; нет данных языка в движке — английская, а не системная */
export function uiDate(value: number | Date, options: Intl.DateTimeFormatOptions): string {
  try { return new Intl.DateTimeFormat([locale, 'en'], options).format(value); } catch { return new Intl.DateTimeFormat('en', options).format(value); }
}
/* legacy-скрипты прототипа (классические теги, без импортов) переводят через этот же словарь */
(globalThis as unknown as { SteosI18n?: object }).SteosI18n = {
  t: uiText, f: uiFormat, pl: uiPlural, date: uiDate, nav: uiNav, in: uiIn, locale: accountLocale,
};
